<template>
    <div class="cohort-trend-chart">
        <div v-if="!hasTrendData" class="cohort-trend-empty">
            <q-icon name="show_chart" size="16px" class="q-mr-xs"/>
            <div>No cohort size trend available for this BCO</div>
        </div>

        <template v-else>
            <PlotlyChart :chart-data="figure" :size="size" :height="height"/>

            <div v-if="weekOnWeekChange" class="cohort-trend-footer">
                <q-icon :name="weekOnWeekChange.icon" size="14px" class="q-mr-xs" :style="`color: ${weekOnWeekChange.color};`"/>
                <span :style="`color: ${weekOnWeekChange.color}; font-weight: 700;`">{{ weekOnWeekChange.label }}</span>
                <span class="q-ml-xs">vs last week</span>
            </div>
        </template>
    </div>
</template>

<script>
import PlotlyChart from "@/components/PlotlyChartsComponent.vue";

const ACCENT = "#ff4e00";
const ACCENT_DARK = "#a94720";
const GRID = "#f0f0f0";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// The weeks arrive as the ISO date of the week start, but anything the agent writes as a plain
// label ("W-3", "Current") is passed straight through.
const formatWeekLabel = (week) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(week);
    if (!match) return week;

    const [, year, month, day] = match;
    return `${Number(day)} ${MONTHS[Number(month) - 1]}`;
};

export default {
    name: "CohortTrendChart",

    components: {
        PlotlyChart,
    },

    props: {
        // { weeks: [...], cohort_sizes: [...] } as stored in BCO_MASTER.HISTORICAL_HCP_COHORT,
        // or the json string of it as it comes back from the DB.
        trend: {
            type: [Object, String],
            default: null,
        },
        size: {
            type: String,
            default: "large",
        },
        height: {
            type: String,
            default: null,
        },
    },

    computed: {
        // Only the weeks that have a cohort size against them make it onto the chart.
        points() {
            let raw = this.trend;

            if (typeof raw === "string") {
                try {
                    raw = JSON.parse(raw);
                } catch (error) {
                    console.error("Invalid cohort size trend json:", error);
                    return { weeks: [], sizes: [] };
                }
            }

            const weeks = Array.isArray(raw?.weeks) ? raw.weeks : [];
            const sizes = Array.isArray(raw?.cohort_sizes) ? raw.cohort_sizes : [];
            const length = Math.min(weeks.length, sizes.length);

            return {
                weeks: weeks.slice(0, length).map((week) => formatWeekLabel(String(week))),
                sizes: sizes.slice(0, length).map((size) => Number(size)),
            };
        },

        hasTrendData() {
            return this.points.sizes.length > 0;
        },

        figure() {
            const { weeks, sizes } = this.points;
            const lastIndex = sizes.length - 1;
            const headroom = Math.max(...sizes, 0) * 1.25 || 10;

            return {
                data: [
                    {
                        type: "scatter",
                        mode: "lines+markers+text",
                        x: weeks,
                        y: sizes,
                        text: sizes.map((size) => String(size)),
                        textposition: "top center",
                        textfont: { color: ACCENT_DARK, size: 11 },
                        cliponaxis: false,
                        line: { color: ACCENT, width: 2.5, shape: "spline", smoothing: 0.5 },
                        // The current week is the last point, drawn heavier than the history behind it.
                        marker: {
                            size: sizes.map((_, index) => (index === lastIndex ? 11 : 7)),
                            color: sizes.map((_, index) => (index === lastIndex ? ACCENT_DARK : ACCENT)),
                            line: { color: "#ffffff", width: 2 },
                        },
                        fill: "tozeroy",
                        fillcolor: "rgba(255, 78, 0, 0.08)",
                        hovertemplate: "%{x}<br><b>%{y}</b> HCPs<extra></extra>",
                    },
                ],
                layout: {
                    showlegend: false,
                    paper_bgcolor: "#ffffff",
                    plot_bgcolor: "#ffffff",
                    hoverlabel: { bgcolor: "#242424", font: { color: "#ffffff", size: 11 } },
                    // Room for the full "Current week" label sitting under the last point.
                    margin: { l: 60, r: 60, t: 40, b: 55 },
                    xaxis: {
                        type: "category",
                        showgrid: false,
                        linecolor: "#dedede",
                        tickfont: { color: "#5f5f5f", size: 11 },
                    },
                    yaxis: {
                        title: { text: "Final HCP cohort", font: { color: "#5f5f5f", size: 11 } },
                        rangemode: "tozero",
                        range: [0, headroom],
                        gridcolor: GRID,
                        zerolinecolor: "#dedede",
                        tickfont: { color: "#5f5f5f", size: 11 },
                        // Invisible outward ticks, purely to hold the labels off the plot area.
                        ticks: "outside",
                        ticklen: 8,
                        tickcolor: "rgba(0, 0, 0, 0)",
                    },
                },
            };
        },

        // A single week of history is all this needs, so it is shown even when the
        // earlier weeks are missing.
        weekOnWeekChange() {
            const { sizes } = this.points;
            if (sizes.length < 2) return null;

            const current = sizes[sizes.length - 1];
            const previous = sizes[sizes.length - 2];
            const delta = current - previous;

            if (delta === 0) {
                return { icon: "remove", color: "#5f5f5f", label: "No change" };
            }

            const percent = previous === 0 ? null : Math.round((delta / previous) * 100);
            const magnitude = `${Math.abs(delta)} HCP${Math.abs(delta) === 1 ? "" : "s"}`;
            const share = percent === null ? "" : ` (${delta > 0 ? "+" : "−"}${Math.abs(percent)}%)`;

            return {
                icon: delta > 0 ? "trending_up" : "trending_down",
                color: delta > 0 ? "#2e7d46" : "#b02a1a",
                label: `${delta > 0 ? "Up" : "Down"} ${magnitude}${share}`,
            };
        },
    },
};
</script>

<style scoped>
.cohort-trend-chart {
    width: 100%;
    max-width: 100%;
    min-width: 0;
}

.cohort-trend-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px 0;
    color: #5f5f5f;
    font-size: 12px;
}

.cohort-trend-footer {
    display: flex;
    align-items: center;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #f1d8cd;
    color: #5f5f5f;
    font-size: 11px;
}
</style>
